import { ref, computed } from '@vue/composition-api';
import { getClusters, addCluster } from '../components/APIManager.js';

// Parameter needed to complete panel intelligence setup
export const REQUIRED_CLUSTER_TYPES = [
  { id: 6, name: 'Target Accounts', label: 'Targets Mapped', entityType: 'publisher', color: 'orange' },
  { id: 2, name: 'Customers', label: 'Customers Mapped', entityType: 'publisher', color: 'green' },
  { id: 3, name: 'Competitors', label: 'Competitors Mapped', entityType: 'supplier', color: 'red' },
  { id: 1, name: 'Partners', label: 'Partners Mapped', entityType: 'supplier', color: 'blue' }
];

// Instructions to complete panel intelligence setup
export const CLUSTER_INSTRUCTIONS = {
  6: {
    title: 'Set Up Target Accounts',
    text: 'Define your target accounts to track opportunities from key buyers. This helps you identify and prioritize contracts from your most important potential customers.',
    importance: 'Target accounts are the buyers you want to win business from. Tracking them helps you stay ahead of opportunities and understand their procurement patterns.'
  },
  2: {
    title: 'Map Your Customers',
    text: 'Map your existing customers to track their contract renewals, new opportunities, and procurement activity. This ensures you never miss a renewal or expansion opportunity.',
    importance: 'Customer mapping helps you maintain relationships, identify upsell opportunities, and protect your existing revenue by tracking contract expirations.'
  },
  3: {
    title: 'Build Your Competitor Cluster',
    text: 'Identify and track your competitors to monitor their wins, contract expirations, and market activity. This intelligence helps you understand competitive threats and opportunities.',
    importance: 'Competitor intelligence is crucial for sellers. It helps you identify when competitors\' contracts are expiring, giving you opportunities to win new business.'
  },
  1: {
    title: 'Find Partners',
    text: 'Build your partner network by identifying potential collaboration opportunities. Partners can help you access new markets, share resources, and win larger contracts together.',
    importance: 'Strategic partnerships can open doors to new opportunities and help you compete for larger contracts that require joint capabilities.'
  }
};

export function useIntelligenceSetup(loadDigestData, showSnackbar) {
  const intelligenceSetup = ref({
    clusters: []
  });
  const bannerDismissed = ref(false);
  const showSetupDialog = ref(false);
  const showClusterSelector = ref(false);
  const currentClusterType = ref(null);
  const intelligenceClusters = ref([]);
  const loadingClusters = ref(false);

  const clusterHasEntities = (cluster) => {
    // search_query is parsed in checkIntelligenceSetup()
    if (!cluster || !cluster.search_query || typeof cluster.search_query !== 'object') {
      return false;
    }

    const query = cluster.search_query;
    if (Object.keys(query).length === 0) {
      return false;
    }

    if (cluster.entity_type === 'publisher') {
      const publisherIds = query.publisher_id || query.buyer_id || [];
      return Array.isArray(publisherIds) && publisherIds.some(id => !!id);
    }

    if (cluster.entity_type === 'supplier') {
      const supplierIds = query.supplier_id || [];
      return Array.isArray(supplierIds) && supplierIds.some(id => !!id);
    }

    return false;
  };

  const getNextIncompleteClusterType = () => {
    // Get cluster types that have clusters WITH entities (not just clusters that exist)
    const typesWithEntities = intelligenceSetup.value.clusters
      .filter(c => c && clusterHasEntities(c))
      .map(c => c.cluster_type_id);
    
    for (const cluster of REQUIRED_CLUSTER_TYPES) {
      // Return the first cluster type that doesn't have entities
      if (!typesWithEntities.includes(cluster.id)) {
        return cluster;
      }
    }
    return null;
  };

  const setupProgress = computed(() => {
    const requiredTypeIds = REQUIRED_CLUSTER_TYPES.map(t => t.id);
    if (!intelligenceSetup.value || !intelligenceSetup.value.clusters || !Array.isArray(intelligenceSetup.value.clusters)) {
      return 0;
    }
    const typesWithEntities = intelligenceSetup.value.clusters
      .filter(c => c && requiredTypeIds.includes(c.cluster_type_id) && clusterHasEntities(c))
      .map(c => c.cluster_type_id);
    const uniqueTypes = [...new Set(typesWithEntities)];
    return Math.round((uniqueTypes.length / REQUIRED_CLUSTER_TYPES.length) * 100);
  });

  const setupComplete = computed(() => {
    const requiredTypeIds = REQUIRED_CLUSTER_TYPES.map(t => t.id);
    if (!intelligenceSetup.value || !intelligenceSetup.value.clusters || !Array.isArray(intelligenceSetup.value.clusters) || intelligenceSetup.value.clusters.length === 0) {
      return false;
    }

    // Group clusters by type
    const clustersByType = {};
    intelligenceSetup.value.clusters.forEach(cluster => {
      if (cluster && cluster.cluster_type_id && requiredTypeIds.includes(cluster.cluster_type_id)) {
        if (!clustersByType[cluster.cluster_type_id]) {
          clustersByType[cluster.cluster_type_id] = [];
        }
        clustersByType[cluster.cluster_type_id].push(cluster);
      }
    });

    // All required types must exist and have at least one cluster with entities
    for (const typeId of requiredTypeIds) {
      const clusters = clustersByType[typeId] || [];
      if (clusters.length === 0) {
        return false;
      }

      const hasEntities = clusters.some(cluster => clusterHasEntities(cluster));
      if (!hasEntities) {
        return false;
      }
    }

    return true;
  });

  const showBanner = computed(() => {
    // Hide banner only if:
    // 1. All 4 cluster types are complete, OR
    // 2. User explicitly dismissed it (bannerDismissed = true)
    // Banner should NOT hide when clicking Continue or opening dialogs
    if (setupComplete.value) {
      return false;
    }
    if (bannerDismissed.value) {
      return false;
    }
    return true;
  });

  const clusterButtons = computed(() => {
    // Get cluster types that have clusters WITH entities (not just clusters that exist)
    if (!intelligenceSetup.value || !intelligenceSetup.value.clusters || !Array.isArray(intelligenceSetup.value.clusters)) {
      // Return default buttons when clusters aren't loaded yet
      return REQUIRED_CLUSTER_TYPES.map(cluster => ({
        ...cluster,
        exists: false,
        status: 'pending',
        color: 'grey',
        icon: null
      }));
    }
    
    const typesWithEntities = intelligenceSetup.value.clusters
      .filter(c => c && clusterHasEntities(c))
      .map(c => c.cluster_type_id);
    
    const nextIncomplete = getNextIncompleteClusterType();

    return REQUIRED_CLUSTER_TYPES.map(cluster => {
      // Check if this cluster type has at least one cluster with entities
      const hasEntities = typesWithEntities.includes(cluster.id);
      const isNext = nextIncomplete && nextIncomplete.id === cluster.id;
      
      return {
        ...cluster,
        exists: hasEntities,
        status: hasEntities ? 'complete' : (isNext ? 'next' : 'pending'),
        color: hasEntities ? 'success' : (isNext ? 'primary' : 'grey'),
        icon: hasEntities ? 'mdi-check' : (isNext ? 'mdi-arrow-right' : null)
      };
    });
  });

  const configuredClustersCount = computed(() => {
    const requiredTypeIds = REQUIRED_CLUSTER_TYPES.map(t => t.id);
    if (!intelligenceSetup.value || !intelligenceSetup.value.clusters || !Array.isArray(intelligenceSetup.value.clusters)) {
      return 0;
    }
    const typesWithEntities = intelligenceSetup.value.clusters
      .filter(c => c && requiredTypeIds.includes(c.cluster_type_id) && clusterHasEntities(c))
      .map(c => c.cluster_type_id);
    return [...new Set(typesWithEntities)].length;
  });

  const checkIntelligenceSetup = async () => {
    try {
      loadingClusters.value = true;
      const requiredTypeIds = REQUIRED_CLUSTER_TYPES.map(t => t.id);
      
      // Fetch both publisher and supplier clusters
      const publisherClusters = await getClusters('publisher');
      const supplierClusters = await getClusters('supplier');
      
      const allClusters = [
        ...(publisherClusters.data || []),
        ...(supplierClusters.data || [])
      ];
      
      // Filter to only required cluster types
      const filteredClusters = allClusters.filter(c => 
        c.cluster_type_id && requiredTypeIds.includes(c.cluster_type_id)
      );
      
      // Parse search_query for each cluster (it comes as a string from API)
      filteredClusters.forEach(cluster => {
        if (cluster.search_query && typeof cluster.search_query === 'string') {
          try {
            cluster.search_query = JSON.parse(cluster.search_query);
          } catch (e) {
            // If parsing fails, set to empty object
            cluster.search_query = {};
          }
        }
        // If search_query is null or undefined, set to empty object
        if (!cluster.search_query) {
          cluster.search_query = {};
        }
      });
      
      intelligenceSetup.value.clusters = filteredClusters;
      intelligenceClusters.value = filteredClusters;
      
      // Reset dismissal on page load (banner reappears if setup incomplete)
      // This ensures banner shows again when user navigates back
    } catch (error) {
      // console.error('Error checking intelligence setup:', error);
    } finally {
      loadingClusters.value = false;
    }
  };

  const createClusterAndOpenSelector = async (clusterType) => {
    try {
      // Check if cluster already exists
      const existing = intelligenceSetup.value.clusters.find(
        c => c.cluster_type_id === clusterType.id
      );
      
      if (existing) {
        // Cluster exists, just open selector
        currentClusterType.value = { ...clusterType, clusterId: existing.id };
        showClusterSelector.value = true;
        return;
      }

      // Create new cluster
      const response = await addCluster(
        clusterType.name,
        clusterType.entityType,
        clusterType.id
      );

      if (response && response.data) {
        // Find the newly created cluster
        const newCluster = response.data.find(
          c => c.cluster_type_id === clusterType.id && c.name === clusterType.name
        );

        if (newCluster) {
          // Refresh setup status
          await checkIntelligenceSetup();
          
          // Open selector with new cluster
          currentClusterType.value = { ...clusterType, clusterId: newCluster.id };
          showClusterSelector.value = true;
        }
      }
    } catch (error) {
      // console.error('Error creating cluster:', error);
      showSnackbar('Failed to create cluster. Please try again.', 'error');
    }
  };

  const onClusterCreated = async () => {
    // Refresh setup status
    await checkIntelligenceSetup();
    
    // Refresh dashboard widgets
    await loadDigestData();
    
    // Close selector dialog
    showClusterSelector.value = false;
    currentClusterType.value = null;
  };

  const dismissBanner = () => {
    // Only dismiss for current page visit, resets when user navigates away and returns
    bannerDismissed.value = true;
  };

  const showContinueSetupDialog = () => {
    const nextType = getNextIncompleteClusterType();
    if (nextType) {
      currentClusterType.value = nextType;
      showSetupDialog.value = true;
    }
  };

  const handleClusterButtonClick = (clusterType) => {
    createClusterAndOpenSelector(clusterType);
  };

  const handleContinueSetup = () => {
    // Close the instruction dialog and open the selector
    // Do NOT dismiss the banner - it should remain visible
    showSetupDialog.value = false;
    if (currentClusterType.value) {
      createClusterAndOpenSelector(currentClusterType.value);
    }
  };

  const onEntitiesAdded = (entities) => {
    // Entities were added to cluster, refresh everything
    onClusterCreated();
    showSnackbar(`Added ${entities.length} ${entities.length === 1 ? 'entity' : 'entities'} to cluster`, 'success');
  };

  const onSelectorSuccess = (message) => {
    showSnackbar(message, 'success');
  };

  const onSelectorError = (message) => {
    showSnackbar(message, 'error');
  };

  const getContinueSetupInstruction = () => {
    if (!currentClusterType.value) return {
      title: 'Continue Setup',
      text: 'Complete your intelligence setup to unlock powerful market insights.',
      importance: 'Setting up all clusters helps you get the most value from the platform.'
    };
    
    return CLUSTER_INSTRUCTIONS[currentClusterType.value.id] || {
      title: 'Continue Setup',
      text: 'Complete your intelligence setup to unlock powerful market insights.',
      importance: 'Setting up all clusters helps you get the most value from the platform.'
    };
  };

  const resetBannerDismissal = () => {
    bannerDismissed.value = false;
  };

  return {
    intelligenceSetup,
    bannerDismissed,
    showSetupDialog,
    showClusterSelector,
    currentClusterType,
    intelligenceClusters,
    loadingClusters,
    setupProgress,
    setupComplete,
    showBanner,
    clusterButtons,
    configuredClustersCount,
    checkIntelligenceSetup,
    createClusterAndOpenSelector,
    onClusterCreated,
    dismissBanner,
    showContinueSetupDialog,
    handleClusterButtonClick,
    handleContinueSetup,
    onEntitiesAdded,
    onSelectorSuccess,
    onSelectorError,
    getContinueSetupInstruction,
    resetBannerDismissal
  };
}
